import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Layout } from "react-grid-layout";

import { useTranslation } from "@/lib/i18n";
import {
  openUrlInPreviewMode,
  showToast,
  WidgetGridLayout,
  confirm,
} from "@/features/shared/components";
import { WIDGET_GRID_COLS } from "@/features/shared/components/widget-grid-layout/widget-grid-layout";
import { AddFab, WidgetContainer } from "@/features/admin/components";
import {
  PageHeader,
  transformWidgetContainersToGridLayout,
  useDeleteNavigationElementMutation,
  usePageData,
  WIDGET_LAYOUT_MIN_H,
  WIDGET_LAYOUT_MIN_W,
} from "@/features/admin/features/pages-management";
import CreateWidgetDialog from "@/features/widget-forms/dialogs/create-widget-dialog";
import { PasteWidgetFab } from "@/features/admin/features/pages-management/components/paste-widget-fab";
import { useExitGuard } from "@/features/admin/hooks/use-exit-guard";
import { useDebouncedLayoutSave } from "@/features/admin/features/pages-management/hooks/use-debounced-layout-save";
import { useGutterResize } from "@/features/admin/features/pages-management/hooks/use-gutter-resize";
import { useLayoutResize } from "@/features/admin/features/pages-management/hooks/use-layout-resize";
import { GutterHandles } from "@/features/admin/features/pages-management/components/gutter-handles";
import {
  compactClearEmptyRows,
  compactLayout,
} from "@/features/admin/features/pages-management/utils/gutters";
import { mergeStructuralLayout } from "@/features/admin/features/pages-management/utils/merge-structural-layout";
import { layoutSignal } from "@/store/layout-signal";

import styles from "./edit-page-layout.module.css";

const EditPagePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { page, parentNavigationElement } = usePageData();

  const deleteNavigationElementMutation = useDeleteNavigationElementMutation();

  const [layout, setLayout] = React.useState<Layout[]>(() =>
    page ? transformWidgetContainersToGridLayout(page.widget_containers) : [],
  );
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const preserveLayoutMinSizes = useCallback((next: Layout[], previous: Layout[]) => {
    return next.map((item) => {
      const prior = previous.find((entry) => entry.i === item.i);
      return {
        ...item,
        minW: prior?.minW ?? item.minW ?? WIDGET_LAYOUT_MIN_W,
        minH: prior?.minH ?? item.minH ?? WIDGET_LAYOUT_MIN_H,
      };
    });
  }, []);

  const commitLayout = useCallback(
    (next: Layout[]) => {
      setLayout((previous) =>
        compactLayout(preserveLayoutMinSizes(next, previous), WIDGET_GRID_COLS),
      );
    },
    [preserveLayoutMinSizes],
  );

  const commitDragLayout = useCallback(
    (next: Layout[]) => {
      setLayout((previous) =>
        compactClearEmptyRows(preserveLayoutMinSizes(next, previous), WIDGET_GRID_COLS),
      );
    },
    [preserveLayoutMinSizes],
  );

  const gridRef = useRef<HTMLDivElement>(null);
  const syncedPageIdRef = useRef<string | null>(null);

  const [createWidgetDialogOpen, setCreateWidgetDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [gridWidth, setGridWidth] = useState(0);

  const {
    activeResize,
    startResize,
    isResizing: isCornerResizing,
  } = useLayoutResize({
    getContainerWidth: () => gridRef.current?.getBoundingClientRect().width ?? 0,
    getLayout: () => layoutRef.current,
    onLayoutChange: commitLayout,
  });

  const {
    gutters,
    activeGutterId,
    squishIds,
    startGutterResize,
    isResizing: isGutterResizing,
  } = useGutterResize({
    layout,
    getContainerWidth: () => gridRef.current?.getBoundingClientRect().width ?? 0,
    onLayoutChange: commitLayout,
  });

  const isInteracting = isDragging || isCornerResizing || isGutterResizing;

  const { isPending, armSave, resetPersistedBaseline, syncPersistedStructure } =
    useDebouncedLayoutSave({
      pageId: page?.id ?? "",
      layout,
      enabled: Boolean(page?.id),
      // Save only starts after the pointer is up (queue coalesces in-flight saves).
      isInteracting,
    });
  useExitGuard(isPending);

  useEffect(() => {
    const element = gridRef.current;
    if (!element) return;

    const updateWidth = () => {
      setGridWidth(element.getBoundingClientRect().width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [page?.id]);

  useEffect(() => {
    const element = gridRef.current;
    if (!element) return;

    const preventSelect = (event: Event) => {
      if (isInteracting) {
        event.preventDefault();
      }
    };

    element.addEventListener("selectstart", preventSelect);
    return () => element.removeEventListener("selectstart", preventSelect);
  }, [isInteracting]);

  const handleResizeStart = useCallback(
    (containerId: string, event: React.PointerEvent<HTMLElement>) => {
      armSave();
      startResize(containerId, event);
    },
    [armSave, startResize],
  );

  const handleGutterResizeStart = useCallback(
    (
      gutter: Parameters<typeof startGutterResize>[0],
      event: React.PointerEvent<HTMLElement>,
    ) => {
      armSave();
      startGutterResize(gutter, event);
    },
    [armSave, startGutterResize],
  );

  const handleDragStart = useCallback(() => {
    armSave();
  }, [armSave]);

  const handleDrag = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragStop = useCallback(() => {
    setIsDragging(false);
  }, []);

  // RGL reports the layout after a drag; collapse fully empty rows only.
  // Cooperative upward slide runs after gutter/corner resize, not drag — otherwise
  // intentional gaps (e.g. C dragged down below B) are undone and never saved.
  const handleLayoutChange = useCallback(
    (next: Layout[]) => {
      // RGL emits this after a drag stops. Clear the visual interaction state
      // here as well as in onDragStop so guides cannot remain visible if a drag
      // stop callback is skipped during a controlled-layout update.
      setIsDragging(false);
      commitDragLayout(next);
      layoutSignal.trigger.updateLayout();
    },
    [commitDragLayout],
  );

  const serverLayout = React.useMemo(
    () =>
      page
        ? transformWidgetContainersToGridLayout(page.widget_containers).sort(byPosition)
        : [],
    [page],
  );

  React.useEffect(() => {
    if (!page) return;

    if (syncedPageIdRef.current !== page.id) {
      syncedPageIdRef.current = page.id;
      setLayout(serverLayout);
      resetPersistedBaseline(serverLayout);
      return;
    }

    // Geometry stays local and is pushed to the backend by the debounced save.
    // Refetches (including the ones our own saves trigger) may only add or
    // remove containers, otherwise the layout would snap back mid-save.
    setLayout((previous) => mergeStructuralLayout(previous, serverLayout));
    syncPersistedStructure(serverLayout);
  }, [page, serverLayout, resetPersistedBaseline, syncPersistedStructure]);

  if (!page) {
    return null;
  }

  const removePage = async () => {
    const result = await confirm(
      t("admin.pages.remove_page_confirm.title"),
      t("admin.pages.remove_page_confirm.body"),
    );

    if (result) {
      deleteNavigationElementMutation.mutate(String(parentNavigationElement?.id), {
        onSuccess: () => {
          showToast(t("admin.messages.page_deleted"));
          navigate("../");
        },
      });
    }
  };

  // Render order follows the layout so that a refetch cannot reshuffle the grid
  // children, which would make react-grid-layout regenerate its layout.
  const layoutOrder = new Map(layout.map((item, index) => [item.i, index]));
  const orderedContainers = [...page.widget_containers].sort(
    (a, b) =>
      (layoutOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (layoutOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );

  // A brand-new container arrives in `page.widget_containers` (and thus as a grid
  // child) one render before the structural effect adds it to `layout` state.
  // Feed react-grid-layout a layout that already covers every child, filling any
  // not-yet-tracked container from the server geometry, so RGL never synthesizes
  // a default 1×1 entry — which would otherwise be committed to local state and
  // lock out the real server size (mergeStructuralLayout then treats the
  // container as already known and keeps the 1×1).
  const localLayoutById = new Map(layout.map((item) => [item.i, item]));
  const serverLayoutById = new Map(serverLayout.map((item) => [item.i, item]));
  const renderLayout = orderedContainers
    .map(({ id }) => localLayoutById.get(id) ?? serverLayoutById.get(id))
    .filter((item): item is Layout => Boolean(item));

  // The same layout on every breakpoint: the grid is 12 columns wide at all
  // widths, and auto-save would otherwise persist a narrow-window preview
  // (every widget full width) as the real layout.
  const layoutVersions = {
    lg: renderLayout,
    md: renderLayout,
    sm: renderLayout,
    xs: renderLayout,
    xxs: renderLayout,
  };
  return (
    <>
      <PageHeader
        navigationElement={parentNavigationElement}
        onOpenPreview={() => openUrlInPreviewMode("")}
        onRemovePage={removePage}
      />

      <Box sx={{ mt: 4, pb: 20 }}>
        <div className={styles.gridWithGuides}>
          <div
            className={`${styles.guides} ${isInteracting ? styles.guidesVisible : ""}`}
            aria-hidden
          >
            {Array.from({ length: WIDGET_GRID_COLS }, (_, index) => (
              <div key={index} className={styles.guideColumn} />
            ))}
          </div>
          <div
            ref={gridRef}
            className={`${styles.gridContent} ${isInteracting ? styles.noSelect : ""}`}
          >
            <WidgetGridLayout
              className="layout"
              layouts={layoutVersions}
              onLayoutChange={handleLayoutChange}
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onDragStop={handleDragStop}
              isDraggable
              isResizable={false}
              compactType={null}
              draggableCancel="[data-no-drag]"
              containerPadding={[0, 0]}
            >
              {orderedContainers.map(({ id, widgets }) => (
                <WidgetContainer
                  key={id}
                  containerId={id}
                  pageId={page.id}
                  widgets={widgets}
                  onResizeStart={handleResizeStart}
                  sizeLabel={
                    activeResize?.i === id
                      ? `${activeResize.w} × ${activeResize.h}`
                      : undefined
                  }
                  isSquished={isGutterResizing && squishIds.has(id)}
                />
              ))}
            </WidgetGridLayout>
            <GutterHandles
              gutters={gutters}
              containerWidth={gridWidth}
              activeGutterId={activeGutterId}
              suppressInteraction={isDragging || isCornerResizing}
              onResizeStart={handleGutterResizeStart}
            />
          </div>
        </div>
      </Box>

      <PasteWidgetFab pageId={page.id} />
      <AddFab
        tooltip={t("admin.pages.detail.add_widget")}
        onClick={() => setCreateWidgetDialogOpen(true)}
      />
      <CreateWidgetDialog
        pageId={page.id}
        open={createWidgetDialogOpen}
        onOpenChange={(value) => setCreateWidgetDialogOpen(value)}
      />
    </>
  );
};

export default EditPagePage;

function byPosition(a: Layout, b: Layout) {
  return a.x - b.x || a.y - b.y;
}
